/* 관계 단언 검증(기획: 그림이 기하학적으로 유효한가). 값 옮기기에서 호스트가 적은 문항의 관계(on, tangent, meet, parallel, perpendicular)를
 * 끝에서 화면 개체의 좌표와 곡선 표본으로 수치 검사한다. 검사할 수 없는 조합은 「검사 불가」로 남기고 실패로 세지 않는다 */
import { engineOf } from './style';
import type { MathcoreApp } from './tools';
import { registryOf } from './tools';
import type { ToolResult } from './tools/types';

export const RELATION_KINDS = ['on', 'tangent', 'meet', 'parallel', 'perpendicular'] as const;
export type RelationKind = typeof RELATION_KINDS[number];
export interface Relation { kind: RelationKind; a: string; b: string; c?: string | null }
export interface RelationResult { relation: Relation; verdict: 'ok' | 'fail' | 'skip'; detail: string }
/** 구성 출처: 어느 도구가 어떤 개체(이름)를 참조해 이 이름의 개체를 세웠는가 */
export interface Provenance { name: string; tool: string; refs: string[] }

/* 구성 도구는 관계를 엔진이 보장한다(pointOnObject 로 세운 점은 그 개체 위, tangentLine 은 그 곡선에 접한다). 곡선 표본은 카메라 창에 따라 성기어 흔들리므로
 * (실측 2026-10-21 experience/run2-export: 같은 호출인데 접점에서 표본까지 0.113 과 7.943) 출처를 1차 증명으로, 수치 검사를 2차로 둔다 */
const PROVING_TOOLS: Record<RelationKind, string[]> = {
  on: ['point.pointOnObject', 'point.intersect', 'point.middlePointBySegment'],
  meet: ['point.intersect'],
  tangent: ['linear.tangentLine'],
  parallel: ['linear.parallel'],
  perpendicular: ['linear.perpendicular', 'linear.perpendicularBisectorBySegment', 'linear.perpendicularBisectorByTwoPoint'],
};

/** 성공한 호출에서 구성 출처를 뽑는다. 인자 속 참조(별칭 o1, 이름 P)는 등록부로 요청한 이름에 되돌린다 */
export function provenanceOf(app: MathcoreApp, results: ToolResult[]): Provenance[] {
  const registry = registryOf(app);
  const nameOfRef = (v: string): string | null => { for (const id of registry.get(v)) { const label = registry.labelOf(id); if (label) return label; } return registry.knownNames().includes(v) ? v : null; };
  const out: Provenance[] = [];
  for (const r of results) {
    if (!r.ok) continue;
    const made = (r.response as { name?: unknown } | undefined)?.name;
    if (typeof made !== 'string' || !made) continue;
    let args: Record<string, unknown> = {};
    try { args = JSON.parse(r.call.function.arguments) as Record<string, unknown>; } catch { continue; }
    const refs: string[] = [];
    for (const [key, value] of Object.entries(args)) {
      if (key === 'name') continue;
      for (const v of Array.isArray(value) ? value : [value]) { if (typeof v === 'string') { const n = nameOfRef(v); if (n && n !== made && !refs.includes(n)) refs.push(n); } }
    }
    out.push({ name: made, tool: r.call.function.name, refs });
  }
  return out;
}

function provedBy(r: Relation, provenance: Provenance[]): Provenance | null {
  const need = [r.b, r.c].filter((n): n is string => Boolean(n));
  return provenance.find((p) => p.name === r.a && PROVING_TOOLS[r.kind].includes(p.tool) && need.every((n) => p.refs.includes(n))) ?? null;
}

type Pt = [number, number];
interface Shape { kind: 'point' | 'linear' | 'curve' | 'circle' | 'other'; point?: Pt; line?: [Pt, Pt]; samples?: Pt[]; center?: Pt; radius?: number }

function shapeOf(app: MathcoreApp, name: string): Shape | null {
  const registry = registryOf(app);
  const o: any = registry.objectOf(name);
  if (!o) return null;
  const fin = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
  if (o.type === 'point' && Array.isArray(o.position) && fin(o.position[0]) && fin(o.position[1])) return { kind: 'point', point: [o.position[0], o.position[1]] };
  const manager: any = engineOf(app)?.objectManager;
  // 선의 두 좌표와 원의 중심, 반지름은 프록시(getAllObjects 의 개체)에 있다(실측 2026-10-21 linear-fields-probe: coordinates, position, radius). 원개체에는 없다
  const raw: any = o;
  if (o.type === 'linear' && Array.isArray(raw?.coordinates) && raw.coordinates.length >= 2) {
    const [p, q] = raw.coordinates;
    if (Array.isArray(p) && Array.isArray(q) && fin(p[0]) && fin(p[1]) && fin(q[0]) && fin(q[1])) return { kind: 'linear', line: [[p[0], p[1]], [q[0], q[1]]] };
  }
  if (o.type === 'polynomial' && !o.inequalityExists) {
    const path: unknown = manager?.getPolynomialPathMap?.(o.id);
    const samples = Array.isArray(path) ? (path as unknown[]).filter((s): s is Pt => Array.isArray(s) && fin(s[0]) && fin(s[1])) : [];
    if (samples.length >= 3) return { kind: 'curve', samples };
  }
  if (String(o.type).startsWith('circle')) {
    const c = raw?.position;
    const r = raw?.radius;
    if (Array.isArray(c) && fin(c[0]) && fin(c[1]) && fin(r)) return { kind: 'circle', center: [c[0], c[1]], radius: r };
  }
  return { kind: 'other' };
}

const dist = (a: Pt, b: Pt): number => Math.hypot(a[0] - b[0], a[1] - b[1]);
const distToLine = (p: Pt, l: [Pt, Pt]): number => { const [a, b] = l; const dx = b[0] - a[0]; const dy = b[1] - a[1]; const len = Math.hypot(dx, dy); return len ? Math.abs((p[0] - a[0]) * dy - (p[1] - a[1]) * dx) / len : dist(p, a); };
/** 점에서 표본 꺾은선까지의 거리. 표본 점만 보면 기울기가 가파른 곳에서 간격이 커 거리가 과장된다(실측 2026-10-21: f(7)=7 인데 표본 거리 0.5) */
const distToSegment = (p: Pt, a: Pt, b: Pt): number => { const dx = b[0] - a[0]; const dy = b[1] - a[1]; const len2 = dx * dx + dy * dy; if (!len2) return dist(p, a); const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2)); return dist(p, [a[0] + t * dx, a[1] + t * dy]); };
const distToCurve = (p: Pt, s: Pt[]): number => { let best = Infinity; for (let i = 0; i + 1 < s.length; i += 1) { if (dist(s[i], s[i + 1]) > 5) continue; const d = distToSegment(p, s[i], s[i + 1]); if (d < best) best = d; } return best === Infinity ? Math.min(...s.map((q) => dist(p, q))) : best; };
const slopeOfLine = (l: [Pt, Pt]): Pt => { const dx = l[1][0] - l[0][0]; const dy = l[1][1] - l[0][1]; const n = Math.hypot(dx, dy) || 1; return [dx / n, dy / n]; };
/** 표본에서 점 p 에 가장 가까운 곳의 접선 방향 */
function curveDirectionAt(samples: Pt[], p: Pt): Pt | null {
  let best = 0; let bestD = Infinity;
  samples.forEach((q, i) => { const d = dist(p, q); if (d < bestD) { bestD = d; best = i; } });
  const a = samples[Math.max(0, best - 1)]; const b = samples[Math.min(samples.length - 1, best + 1)];
  const dx = b[0] - a[0]; const dy = b[1] - a[1]; const n = Math.hypot(dx, dy);
  return n ? [dx / n, dy / n] : null;
}

/** 허용오차: 장면 크기의 2% (최소 0.05) */
function toleranceOf(app: MathcoreApp): number {
  const pts: Pt[] = ((app.objectManager?.getAllObjects?.() ?? []) as any[]).filter((o) => o.type === 'point' && Array.isArray(o.position)).map((o) => [o.position[0], o.position[1]] as Pt);
  const xs = pts.map((p) => p[0]); const ys = pts.map((p) => p[1]);
  const span = pts.length ? Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) : 0;
  return Math.max(0.05, span * 0.02);
}

function onShape(p: Pt, x: Shape, tol: number): { ok: boolean; d: number } | null {
  if (x.kind === 'linear' && x.line) { const d = distToLine(p, x.line); return { ok: d <= tol, d }; }
  if (x.kind === 'curve' && x.samples) { const d = distToCurve(p, x.samples); return { ok: d <= tol * 2, d }; }
  if (x.kind === 'circle' && x.center && x.radius != null) { const d = Math.abs(dist(p, x.center) - x.radius); return { ok: d <= tol, d }; }
  return null;
}

export function checkRelations(app: MathcoreApp, relations: Relation[], provenance: Provenance[] = []): RelationResult[] {
  const tol = toleranceOf(app);
  const out: RelationResult[] = [];
  for (const r of relations) {
    const A = shapeOf(app, r.a); const B = shapeOf(app, r.b); const C = r.c ? shapeOf(app, r.c) : null;
    const skip = (why: string) => out.push({ relation: r, verdict: 'skip', detail: why });
    if (!A || !B || (r.c && !C)) { skip('이름의 개체가 화면에 없다'); continue; }
    const proof = provedBy(r, provenance);
    if (proof) { out.push({ relation: r, verdict: 'ok', detail: `구성 증명: ${r.a} 는 ${proof.tool}(${proof.refs.join(', ')}) 로 세웠다` }); continue; }
    try {
      if (r.kind === 'on') {
        if (A.kind !== 'point' || !A.point) { skip('첫째가 점이 아니다'); continue; }
        const got = onShape(A.point, B, tol);
        if (!got) { skip(`${r.b} 의 종류는 검사 못 함`); continue; }
        out.push({ relation: r, verdict: got.ok ? 'ok' : 'fail', detail: `점 ${r.a} 와 ${r.b} 의 거리 ${got.d.toFixed(3)} (허용 ${tol.toFixed(3)}${B.kind === 'curve' ? `, 표본 ${B.samples?.length ?? 0}개` : ''})` });
      } else if (r.kind === 'meet') {
        if (A.kind !== 'point' || !A.point || !C) { skip('meet 는 점, 개체, 개체'); continue; }
        const g1 = onShape(A.point, B, tol); const g2 = onShape(A.point, C, tol);
        if (!g1 || !g2) { skip('종류를 검사 못 함'); continue; }
        out.push({ relation: r, verdict: g1.ok && g2.ok ? 'ok' : 'fail', detail: `점 ${r.a}: ${r.b} 까지 ${g1.d.toFixed(3)}, ${r.c} 까지 ${g2.d.toFixed(3)} (허용 ${tol.toFixed(3)})` });
      } else if (r.kind === 'tangent') {
        // tangent(l, f, P): l 은 선, f 는 곡선이나 원, P 는 접점
        if (A.kind !== 'linear' || !A.line || !C || C.kind !== 'point' || !C.point) { skip('tangent 는 선, 곡선, 접점'); continue; }
        const onL = onShape(C.point, A, tol); const onF = onShape(C.point, B, tol);
        if (!onL || !onF) { skip('종류를 검사 못 함'); continue; }
        let dirOk: boolean | null = null; let angle = 0;
        const ld = slopeOfLine(A.line);
        if (B.kind === 'curve' && B.samples) { const cd = curveDirectionAt(B.samples, C.point); if (cd) { const cross = Math.abs(ld[0] * cd[1] - ld[1] * cd[0]); angle = Math.asin(Math.min(1, cross)) * 180 / Math.PI; dirOk = angle <= 3; } }
        if (B.kind === 'circle' && B.center) { const rad: Pt = [C.point[0] - B.center[0], C.point[1] - B.center[1]]; const n = Math.hypot(rad[0], rad[1]) || 1; const dot = Math.abs(ld[0] * rad[0] / n + ld[1] * rad[1] / n); angle = Math.asin(Math.min(1, dot)) * 180 / Math.PI; dirOk = angle <= 3; }
        const ok = onL.ok && onF.ok && dirOk !== false;
        out.push({ relation: r, verdict: ok ? 'ok' : 'fail', detail: `접점 ${r.c}: 선까지 ${onL.d.toFixed(3)}, 곡선까지 ${onF.d.toFixed(3)}${dirOk === null ? '' : `, 방향 차 ${angle.toFixed(1)}도`}${B.kind === 'curve' ? ` (표본 ${B.samples?.length ?? 0}개)` : ''}` });
      } else if (r.kind === 'parallel' || r.kind === 'perpendicular') {
        if (A.kind !== 'linear' || !A.line || B.kind !== 'linear' || !B.line) { skip('두 선이 아니다'); continue; }
        const u = slopeOfLine(A.line); const v = slopeOfLine(B.line);
        const cross = Math.abs(u[0] * v[1] - u[1] * v[0]); const dot = Math.abs(u[0] * v[0] + u[1] * v[1]);
        const angle = Math.atan2(cross, dot) * 180 / Math.PI;
        const ok = r.kind === 'parallel' ? angle <= 1 : Math.abs(angle - 90) <= 1;
        out.push({ relation: r, verdict: ok ? 'ok' : 'fail', detail: `${r.a} 와 ${r.b} 의 각 ${angle.toFixed(1)}도` });
      }
    } catch (error) { skip(`검사 중 오류: ${error instanceof Error ? error.message : String(error)}`); }
  }
  return out;
}
